import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tournament,
  TournamentBracket,
  WinnerModel,
  TournamentClient,
  BracketClient,
} from '../../utils/apiClient';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { formatAgeCategoryLabel, formatWeightCategoryLabel } from '../../utils/categoryResolver';
import { getTournamentLifecycle } from '../../utils/tournamentLifecycle';

interface BracketWinner {
  bracketId: number;
  ageCategoryName: string;
  weightCategoryName: string;
  winner: WinnerModel;
}

const TournamentResults: React.FC = () => {
  const appContext = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [brackets, setBrackets] = useState<TournamentBracket[]>([]);
  const [winners, setWinners] = useState<BracketWinner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const tournamentClient = new TournamentClient(ApiUrl, axios);
  const bracketClient = new BracketClient(ApiUrl, axios);

  useEffect(() => {
    const fetchTournamentData = async () => {
      try {
        setLoading(true);
        
        // Загрузка деталей турнира
        const tournamentData = await tournamentClient.getTournament(parseInt(id!));
        setTournament(tournamentData);
        
        if (getTournamentLifecycle(tournamentData) !== 'finished') {
          navigate(`/tournaments/${id}`);
          return;
        }
        
        // Загрузка сеток
        const bracketsData = await bracketClient.getBrackets(parseInt(id!));
        setBrackets(bracketsData);
        
        // Загрузка победителей для каждой сетки
        const winnerPromises = bracketsData.map(async (bracket) => {
          try {
            const winner = await bracketClient.getBracketWinner(bracket.id!);
            const ageCat = bracket.ageCategoryInTournament;
            const weightCat = bracket.weightCategoryInTournament;
            return {
              bracketId: bracket.id!,
              ageCategoryName: ageCat
                ? formatAgeCategoryLabel(ageCat, tournamentData.ageCategories ?? [])
                : 'Неизвестно',
              weightCategoryName: weightCat
                ? formatWeightCategoryLabel(weightCat, tournamentData.weightCategories ?? [])
                : 'Неизвестно',
              winner
            };
          } catch (error) {
            console.error(`Ошибка загрузки победителя для сетки ${bracket.id}:`, error);
            return null;
          }
        });
        
        const winnerResults = await Promise.all(winnerPromises);
        setWinners(winnerResults.filter(w => w !== null) as BracketWinner[]);
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTournamentData();
    }
  }, [id, navigate]);

  if (loading) {
    return <div>Загрузка результатов турнира...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!tournament) {
    return <div>Турнир не найден</div>;
  }

  return (
    <div className="tournament-results">
      <h2>Результаты турнира: {tournament.name}</h2>
      
      <div className="tournament-info">
        <p><strong>Место проведения:</strong> {tournament.location}</p>
        <p><strong>Даты:</strong> {new Date(tournament.startDate).toLocaleDateString()} - {new Date(tournament.endDate).toLocaleDateString()}</p>
        <p><strong>Формат:</strong> определяется отдельно для каждой пары категорий (возраст + вес).</p>
      </div>
      
      <div className="results-section">
        <h3>Победители по категориям</h3>
        
        {winners.length === 0 ? (
          <p>Нет данных о победителях</p>
        ) : (
          <div className="winners-table">
            <table>
              <thead>
                <tr>
                  <th>Возрастная категория</th>
                  <th>Весовая категория</th>
                  <th>Победитель</th>
                  <th>Спортивная школа</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {winners.map((bracketWinner) => (
                  <tr key={bracketWinner.bracketId}>
                    <td>{bracketWinner.ageCategoryName}</td>
                    <td>{bracketWinner.weightCategoryName}</td>
                    <td>
                      <div className="winner-name">{bracketWinner.winner.personName}</div>
                    </td>
                    <td>{bracketWinner.winner.organization}</td>
                    <td>
                      <button 
                        onClick={() => navigate(`/brackets/${bracketWinner.bracketId}`)}
                        className="btn btn--view"
                      >
                        Просмотр сетки
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      <div className="brackets-section">
        <h3>Все категории</h3>
        
        {brackets.length === 0 ? (
          <p>Нет данных о категориях</p>
        ) : (
          <div className="brackets-list">
            <table>
              <thead>
                <tr>
                  <th>Возрастная категория</th>
                  <th>Весовая категория</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {brackets.map((bracket) => (
                  <tr key={bracket.id}>
                    <td>
                      {bracket.ageCategoryInTournament
                        ? formatAgeCategoryLabel(bracket.ageCategoryInTournament, tournament.ageCategories ?? [])
                        : 'Неизвестно'}
                    </td>
                    <td>
                      {bracket.weightCategoryInTournament
                        ? formatWeightCategoryLabel(bracket.weightCategoryInTournament, tournament.weightCategories ?? [])
                        : 'Неизвестно'}
                    </td>
                    <td>
                      <button 
                        onClick={() => navigate(`/brackets/${bracket.id}`)}
                        className="btn btn--view"
                      >
                        Просмотр сетки
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      <div className="action-buttons">
        <button onClick={() => navigate('/tournaments')} className="btn btn--back">
          К списку турниров
        </button>
        
        <button onClick={() => navigate(`/tournaments/${id}`)} className="btn">
          Детали турнира
        </button>
      </div>
    </div>
  );
};

export default TournamentResults;

// Дополнительные CSS стили для результатов турнира
const styles = `
.tournament-results {
  background-color: white;
  padding: 2rem;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.results-section, .brackets-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.winner-name {
  font-weight: bold;
  color: #10b981;
}

.details-button {
  background-color: #4cc9f0;
}

.winners-table td, .brackets-list td {
  vertical-align: middle;
}

.winners-table tr:hover, .brackets-list tr:hover {
  background-color: #f9f9f9;
}
`;
